'use client';

import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SearchPriceInput } from '@/components/form/search-price-input';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateOffer } from '@/lib/api/offers';

type OfferType = 'flash' | 'bundle' | 'club';
const OFFER_TYPES: OfferType[] = ['flash', 'bundle', 'club'];

interface SetupUpsellOfferCreateFormProps {
  storeId: string;
  /** Fires with the new offer id once creation succeeds. */
  onCreated: (offerId: string) => void;
  /** Shown only when at least one upsell already exists (lets the merchant back out). */
  onCancel?: () => void;
}

/**
 * Inline create form for a guided-setup upsell. Mirrors the funnel editor's
 * InlineOfferCreateForm — a name, a type (flash/bundle/club), and a single
 * product/price line item — persisted as an `offers` entity.
 */
export function SetupUpsellOfferCreateForm({ storeId, onCreated, onCancel }: SetupUpsellOfferCreateFormProps) {
  const { t } = useTranslation('setup');
  const createOffer = useCreateOffer();
  const [name, setName] = React.useState('');
  const [type, setType] = React.useState<OfferType>('flash');
  const [priceId, setPriceId] = React.useState('');

  const isValid = name.trim().length > 0 && priceId.length > 0;

  const handleCreate = async () => {
    if (!isValid || createOffer.isPending) return;
    try {
      const result = await createOffer.mutateAsync({
        storeId,
        type,
        titleTrans: { en: name.trim() },
        lineItems: [{ priceId, quantity: 1 }],
      });
      if (result?.id) onCreated(result.id);
    } catch {
      // Error surfaced via the mutation hook's toast.
    }
  };

  return (
    <div className="border-primary/30 bg-primary/5 flex flex-col gap-4 rounded-lg border p-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="min-w-0 flex-1 space-y-1.5">
          <Label htmlFor="setup-upsell-offer-name" className="text-sm">
            {t('experience.checkout.upsellOffer.create.nameLabel')}
          </Label>
          <Input
            id="setup-upsell-offer-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('experience.checkout.upsellOffer.create.namePlaceholder')}
          />
        </div>
        <div className="space-y-1.5 sm:w-36">
          <Label className="text-sm">{t('experience.checkout.upsellOffer.create.typeLabel')}</Label>
          <Select value={type} onValueChange={(value) => setType(value as OfferType)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OFFER_TYPES.map((offerType) => (
                <SelectItem key={offerType} value={offerType}>
                  {t(`experience.checkout.upsellOffer.create.types.${offerType}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* relative z-20 lifts the SearchPriceInput dropdown above the section's
          sticky z-10 footer — its own high z-index is trapped by an `isolate`
          stacking context, so the field group must out-rank the footer here. */}
      <div className="relative z-20 space-y-1.5">
        <Label className="text-sm">{t('experience.checkout.upsellOffer.create.productLabel')}</Label>
        <SearchPriceInput
          storeId={storeId}
          includeInactive
          priceId={priceId || undefined}
          onChange={(selection) => setPriceId(selection.priceId)}
        />
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          onClick={handleCreate}
          disabled={!isValid || createOffer.isPending}
          className="gap-2"
        >
          {createOffer.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          {createOffer.isPending
            ? t('experience.checkout.upsellOffer.create.creating')
            : t('experience.checkout.upsellOffer.create.submit')}
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('experience.checkout.upsellOffer.create.cancel')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
